import {
  getDocumentQueryTypePrefix,
  getDocumentQueryValuesPrefix,
} from '../../helper/document-query/document-query-prefix.function';

function getBaseVisibleColumns(prefix: string, valuesPrefix: string): string[] {
  return [
    'sales_organisation.sales_organisation',
    `${prefix}.document_number`,
    `${prefix}.document_date`,
    `${prefix}.status`,
    'customer.number',
    'customer.sub_number',
    `${prefix}.customer_name`,
    `${prefix}.construction_site`,
    'invoice_customer.number',
    'invoice_customer.name',
    `${valuesPrefix}.value`,
    `${valuesPrefix}.tax_value`,
    `${valuesPrefix}.cost_of_goods`,
    `${valuesPrefix}.contribution_margin_percent`,
    `${valuesPrefix}.contribution_margin`,
    'sales_representative.name',
    '_user.user_full_name',
    'project.project',
    'project.name',
    'distribution_type_language.name',
  ];
}

export function getVisibleColumns(documentType: string): Set<string> {
  const prefix = getDocumentQueryTypePrefix(documentType);
  const valuesPrefix = getDocumentQueryValuesPrefix(documentType);
  const base = getBaseVisibleColumns(prefix, valuesPrefix);
  switch (documentType) {
    case 'OFFER':
      return new Set([
        'offer.icons',
        'sales_organisation.sales_organisation',
        `${prefix}.document_number`,
        `${prefix}.document_date`,
        'customer.number',
        'customer.sub_number',
        `${prefix}.customer_name`,
        'project.project',
        `${prefix}.construction_site`,
        'invoice_customer.number',
        'invoice_customer.name',
        'sales_representative.name',
        `${prefix}.status`,
        'offer.lost',
        'currency.currency',
        `${valuesPrefix}.value`,
        `${valuesPrefix}.cost_of_goods`,
        `${valuesPrefix}.contribution_margin`,
        `${valuesPrefix}.contribution_margin_percent`,
        '_user.user_full_name',
        'offer.valid_until',
        'offer.creation_time',
        'offer.id',
        'offer.barcode',
      ]);
    case 'BLANKET_ORDER':
      return new Set([
        'blanket_order.icons',
        'sales_organisation.sales_organisation',
        `${prefix}.document_number`,
        `${prefix}.document_date`,
        'customer.number',
        'customer.sub_number',
        `${prefix}.customer_name`,
        'project.project',
        'invoice_customer.number',
        'invoice_customer.name',
        'blanket_order.valid_until',
        `${prefix}.status`,
        'sales_representative.name',
        'currency.currency',
        `${valuesPrefix}.tax_value`,
        `${valuesPrefix}.value`,
        `${valuesPrefix}.cost_of_goods`,
        `${valuesPrefix}.contribution_margin`,
        `${valuesPrefix}.contribution_margin_percent`,
        '_user.user_full_name',
        'blanket_order.id',
      ]);
    case 'SALES_ORDER':
      return new Set([
        'sales_order.icons',
        'sales_organisation.sales_organisation',
        `${prefix}.document_number`,
        `${prefix}.document_date`,
        'customer.number',
        'customer.sub_number',
        `${prefix}.customer_name`,
        'invoice_customer.number',
        'invoice_customer.name',
        'project.project',
        `${prefix}.construction_site`,
        `${prefix}.status`,
        'currency.currency',
        `${valuesPrefix}.value`,
        `${valuesPrefix}.tax_value`,
        `${valuesPrefix}.cost_of_goods`,
        `${valuesPrefix}.contribution_margin`,
        `${valuesPrefix}.contribution_margin_percent`,
        'distribution_type_language.name',
        'warehouse.warehouse',
        'warehouse.name',
        '_user.user_full_name',
        'sales_order.creation_time',
        'sales_order.barcode',
        'sales_order.id',
      ]);

    case 'INVOICE':
    case 'CASH_SALE_INVOICE':
    case 'CREDIT_INVOICE':
      return new Set([
        'invoice.icons',
        'sales_organisation.sales_organisation',
        `${prefix}.document_number`,
        'invoice.reference_number',
        `${prefix}.document_date`,
        'customer.number',
        'customer.sub_number',
        `${prefix}.customer_name`,
        'project.project',
        `${prefix}.construction_site`,
        'invoice_customer.number',
        'invoice_customer.name',
        'invoice.invoice_sub_type',
        'currency.currency',
        `${valuesPrefix}.value`,
        `${valuesPrefix}.tax_value`,
        `${valuesPrefix}.cost_of_goods`,
        `${valuesPrefix}.contribution_margin`,
        `${valuesPrefix}.contribution_margin_percent`,
        'invoice.days1_invoice',
        'invoice.due_date',
        'invoice.discount1_invoice',
        'invoice.days_net_invoice',
        'invoice.paid',
        'invoice.accounting_indicator',
        'invoice.accounting_date',
        'invoice.period_date',
        '_user.user_full_name',
        'invoice.creation_time',
        'invoice.id',
      ]);
    case 'DELIVERY_NOTE':
      return new Set([
        'delivery_note.icons',
        'sales_organisation.sales_organisation',
        `${prefix}.document_number`,
        `${prefix}.document_date`,
        'customer.number',
        'customer.sub_number',
        `${prefix}.customer_name`,
        'invoice_customer.number',
        'invoice_customer.name',
        'project.project',
        `${prefix}.construction_site`,
        'delivery_note.invoice_approval',
        `${prefix}.status`,
        'sales_representative.name',
        'terms_of_payment.discount1',
        'driver.driver_number',
        'vehicle.vehicle',
        'delivery_note.delivery_date',
        'delivery_note.invoice_date',
        'distribution_type_language.name',
        'warehouse.warehouse',
        'warehouse.name',
      ]);
    case 'SALES_VOUCHER':
      return new Set([
        'sales_organisation.sales_organisation',
        'sales_voucher.receipt_number',
        `${prefix}.document_date`,
        'customer.number',
        'customer.sub_number',
        `${prefix}.customer_name`,
        'sales_voucher.receipt_type',
        'register.register_number',
        'cashier_language.name',
        'project.project',
        'currency.currency',
        `${valuesPrefix}.value`,
        `${valuesPrefix}.tax_value`,
        `${valuesPrefix}.cost_of_goods`,
        `${valuesPrefix}.contribution_margin`,
        `${valuesPrefix}.contribution_margin_percent`,
        'sales_voucher_values.income',
        'sales_voucher_values.expenses',
        'payment.paid',
        'sales_representative.name',
        'warehouse.warehouse',
        'warehouse.name',
        '_user.user_full_name',
        'sales_voucher.print_date',
        'sales_voucher.id',
      ]);
    case 'COMMISSION_NOTE':
      return new Set([
        'commission_note.icons',
        'sales_organisation.sales_organisation',
        'distribution_type_language.name',
        `${prefix}.document_number`,
        `${prefix}.document_date`,
        'customer.number',
        'customer.sub_number',
        `${prefix}.customer_name`,
        'invoice_customer.number',
        'invoice_customer.name',
        'sales_representative.name',
        'project.project',
        'project.name',
        `${prefix}.construction_site`,
        `${prefix}.status`,
        'warehouse.warehouse',
        'warehouse.name',
        'currency.currency',
        `${valuesPrefix}.value`,
        `${valuesPrefix}.contribution_margin`,
        `${valuesPrefix}.contribution_margin_percent`,
        `${valuesPrefix}.cost_of_goods`,
        'project_source.project_source',
        'cost_center_source.cost_center_source',
        '_user.user_full_name',
        'commission_note.creation_time',
      ]);
    case 'ROUTING_SLIP':
      return new Set([
        'sales_organisation.sales_organisation',
        'distribution_type_language.name',
        `${prefix}.document_number`,
        `${prefix}.document_date`,
        'customer.number',
        'customer.sub_number',
        `${prefix}.customer_name`,
        'invoice_customer.number',
        'invoice_customer.name',
        `${prefix}.status`,
        'currency.currency',
        `${valuesPrefix}.value`,
        '_user.user_full_name',
      ]);
    case 'MATERIAL_DELIVERY_NOTE':
      return new Set([
        'delivery_note.icons',
        'sales_organisation.sales_organisation',
        'delivery_note.document_number',
        'delivery_note.document_date',
        'customer.number',
        'customer.sub_number',
        `${prefix}.customer_name`,
        'invoice_customer.number',
        'invoice_customer.name',
        'project.project',
        'delivery_note.construction_site',
        'delivery_note.invoice_approval',
        'delivery_note.status',
        'sales_representative.name',
        'terms_of_payment.discount1',
        'driver.driver_number',
        'vehicle.vehicle',
        'delivery_note.delivery_date',
        'delivery_note.invoice_date',
        'distribution_type_language.name',
        'warehouse.warehouse',
        'warehouse.name',
      ]);
    case 'CONSOLIDATED_MEASUREMENT_REPORT':
      return new Set([
        'delivery_note.icons',
        'sales_organisation.sales_organisation',
        'delivery_note.document_number',
        'delivery_note.document_date',
        'customer.number',
        'customer.sub_number',
        `${prefix}.customer_name`,
        'invoice_customer.number',
        'invoice_customer.name',
        'sales_representative.name',
        'terms_of_payment.discount1',
        'driver.driver_number',
        'vehicle.vehicle',
        'delivery_note.delivery_date',
        'distribution_type_language.name',
        'warehouse.warehouse',
        'warehouse.name',
        'delivery_note.construction_site',
      ]);
    default:
      return new Set(base);
  }
}
